import Link from 'next/link'
import { CalendarDays, Map, MapPinned, User } from 'lucide-react'

import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import { convertLexicalToPlaintext } from '@payloadcms/richtext-lexical/plaintext'

import Pill from '@/components/Pill'
import { Button } from '@/components/ui/button'
import { formatDateTime } from '@/utilities/global/formatDateTime'
import { usStates } from '@/utilities/states'
import { cn } from '@/utilities/ui'

import type { Topic, Win } from '@/payload-types'

interface WinTileProps {
  win: Win
  className?: string
}

// Extended Win type to include topics (populated via depth: 1)
type WinWithTopics = Win & { topics?: (number | Topic)[] | null }

const WinTile: React.FC<WinTileProps> = ({ win, className }) => {
  // Get the win's own topics (already populated via depth: 1)
  const winTopics = (win as WinWithTopics).topics ?? []

  const plaintext = convertLexicalToPlaintext({
    data: win.winsDescription as SerializedEditorState,
  })

  return (
    <article
      className={cn(
        'win-tile flex w-full max-w-full flex-col overflow-hidden rounded-2xl bg-card p-4 shadow-border lg:p-8',
        className,
      )}
    >
      <div className="flex grow flex-col gap-2 md:gap-6">
        {/* Top section */}
        <div className="flex flex-col gap-4">
          <h3 className="line-clamp-3 overflow-hidden font-family-display text-xl leading-7 text-ellipsis text-foreground lg:text-2xl lg:leading-9">
            {win.title}
          </h3>
          <p className="line-clamp-3 h-18 font-family-body text-sm leading-6 text-ellipsis text-card-foreground lg:text-base">
            {win.shortDescription
              ? win.shortDescription
              : win.winsDescription
                ? plaintext.slice(0, 200)
                : 'No description available'}
          </p>
        </div>

        {/* Middle section: State and MAHA */}
        <div className="flex flex-col gap-3">
          <div className="flex gap-6">
            <p className="flex items-center gap-1 text-sm leading-5 font-medium text-neutral-500">
              {win.state ? (
                <>
                  <MapPinned size={16} />
                  {usStates.find((state) => state.value === win.state)?.label}
                </>
              ) : (
                <>
                  <Map size={16} />
                  National
                </>
              )}
            </p>
            <p className="flex items-center gap-1 text-sm leading-5 font-medium text-neutral-500">
              <User size={16} />
              {win.state ? usStates.find((state) => state.value === win.state)?.label : ''}
              &nbsp;MAHA
            </p>
          </div>

          {/* <CategoryIcons
            allTopics={winTopics as Topic[]}
            specificTopics={winTopics as Topic[]}
          /> */}
          {winTopics.length > 0 && (
            <div
              className="scrollbar-hidden -mr-4 flex items-center justify-start gap-2 overflow-x-auto pr-4 lg:-mr-8 lg:pr-8"
              data-lenis-prevent-horizontal
            >
              {winTopics.map((topic) => {
                return (
                  <Pill
                    key={(topic as Topic).id}
                    color={(topic as Topic).color ?? 'accent-foreground-3'}
                  >
                    {(topic as Topic).title}
                  </Pill>
                )
              })}
            </div>
          )}
        </div>

        {/* Bottom section: Date and read more button */}
        <div className="mt-auto flex justify-between">
          <p className="flex items-center gap-1 py-2 text-sm leading-5 font-medium text-neutral-500">
            <CalendarDays size={16} />
            {formatDateTime(win.winDate || win.createdAt)}
          </p>
          {win.winLink && (
            <Button variant="link" asChild>
              <Link href={win.winLink} target="_blank" className="flex items-center gap-2">
                Read More
              </Link>
            </Button>
          )}
        </div>
      </div>
    </article>
  )
}

export default WinTile
